import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Form, Input, Button, message } from "antd";
import axios from "axios";
import { getAuth } from "firebase/auth";
import "./styles/PaymentPage.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51QF9WgIjvnrHHtF1y7CdWvUDCX6Y5FuKS6QbmIR3lDEWgwZaKjUA4gLX0DIDHiS2zd6B8MIFCJLEhgxw2KCCoveG00cyYtIl7c"
);

const CheckoutForm = ({ clientSecret, campaignId, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showNotification, setShowNotification] = useState(false);

  const handleSubmit = async (values) => {
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: values.name,
            email: values.email,
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      if (result.paymentIntent.status === "succeeded") {
        // ⏱️ START TIMING TEST
        const startTime = performance.now();
        console.log('\n💳 ========================================');
        console.log('💳 [PaymentSuccess] Stripe payment confirmed at 0ms');
        console.log(`💳 [PaymentSuccess] Campaign ID: ${campaignId}`);
        console.log(`💳 [PaymentSuccess] Payment Intent ID: ${result.paymentIntent.id}`);
        console.time('[E2E] Full payment confirmation time');
        
        // 1️⃣ ⚡ INSTANT UI UPDATE: Optimistically update cache immediately
        // This makes the UI feel instant (<100ms) - mirrors MEO behavior
        if (window.updateCampaignInCache) {
          const updateStartTime = performance.now();
          const success = window.updateCampaignInCache(campaignId, (campaign) => ({
            ...campaign,
            paymentStatus: 'paid',
            status: 'pending',
            // Explicitly set recipients to empty to show "Waiting..." message
            recipients: campaign.recipients || [],
            totalRecipients: campaign.totalRecipients || 0,
          }));
          const updateDuration = performance.now() - updateStartTime;
          
          if (success) {
            console.log(`⚡ [OptimisticUpdate] Cache updated in ${updateDuration.toFixed(2)}ms`);
          } else {
            console.warn(`⚠️ [OptimisticUpdate] Cache update returned false - campaign may not be cached yet`);
          }
        } else {
          console.error('❌ [OptimisticUpdate] window.updateCampaignInCache not available!');
          console.error('❌ Make sure initGlobalOptimisticUpdate() was called in campaigns list component');
        }
        
        // 2️⃣ ✅ BACKEND CONFIRMATION: Wait for Firestore update before navigation
        // This ensures data persistence and prevents UI revert
        try {
          const backendStartTime = performance.now();
          console.log(`🔄 [Backend] Calling /api/payment/mark-campaign-paid...`);
          
          const markResponse = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/mark-campaign-paid`,
            { campaignId, paymentIntentId: result.paymentIntent.id },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "Content-Type": "application/json",
              },
            }
          );
          
          const backendDuration = performance.now() - backendStartTime;
          
          if (markResponse.data.success) {
            console.log(`✅ [Backend] Confirmation completed in ${backendDuration.toFixed(2)}ms`);
            console.log(`✅ [Backend] Response:`, markResponse.data);
            console.log(`✅ [Backend] Firestore updated: paymentStatus="paid", status="pending"`);
          } else {
            console.error("❌ [Backend] Failed to mark campaign as paid:", markResponse.data.error);
            message.error('Payment successful but status update failed. Please contact support.');
          }
        } catch (markErr) {
          const backendDuration = performance.now() - startTime;
          console.error("❌ [Backend] Failed to mark campaign as paid:", {
            error: markErr.message,
            status: markErr.response?.status,
            statusText: markErr.response?.statusText,
            data: markErr.response?.data,
            duration: `${backendDuration.toFixed(2)}ms`
          });
          console.error("❌ [Backend] Full error:", markErr);
          message.error('Payment successful but status update failed. Please contact support.');
        }
        
        // 3️⃣ Trigger cache invalidation event (fetch fresh data after backend confirmation)
        if (window.triggerCampaignRefresh) {
          window.triggerCampaignRefresh('payment-success');
          console.log('🔄 [CacheRefresh] Campaign refresh event triggered');
        } else {
          console.warn('⚠️ [CacheRefresh] window.triggerCampaignRefresh not available');
          console.warn('⚠️ Fresh data will not be fetched automatically');
        }
        
        // 4️⃣ Navigate to campaigns page (after backend confirmation)
        const totalDuration = performance.now() - startTime;
        console.log(`⚡ [Navigation] Redirecting to /campaigns at ${totalDuration.toFixed(2)}ms`);
        console.timeEnd('[E2E] Full payment confirmation time');
        console.log('💳 ========================================\n');
        navigate("/campaigns");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
      console.error("❌ Payment error:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    let timeout;
    if (showNotification) {
      // Poll campaign status every 30 seconds
      interval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/status/${campaignId}`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            }
          );
          console.log("Polling status:", response.data.data?.status, "paymentStatus:", response.data.data?.paymentStatus);
          
          // Check if payment is completed (status changed from draft to pending)
          if (response.data.success && response.data.data?.paymentStatus === "paid" && response.data.data?.status === "pending") {
            console.log("✅ Payment completed, campaign is now pending processing");
            setShowNotification(false);
            navigate("/campaigns"); // Navigate to campaigns page
            clearInterval(interval);
            if (timeout) clearTimeout(timeout);
          }
          
          // Also check for completed status (after processing)
          if (response.data.success && response.data.data?.status === "completed") {
            console.log("✅ Campaign processing completed");
            setShowNotification(false);
            navigate("/campaigns"); // Navigate to campaigns page
            clearInterval(interval);
            if (timeout) clearTimeout(timeout);
          }
        } catch (err) {
          console.error("❌ Polling error:", err.message);
          if (err.response?.status === 404) {
            console.error("❌ Campaign not found:", campaignId);
            setShowNotification(false);
            clearInterval(interval);
            if (timeout) clearTimeout(timeout);
          }
        }
      }, 30000);

      // Fallback 12-minute timeout
      timeout = setTimeout(() => {
        console.log("⏰ 12-minute timeout triggered");
        setShowNotification(false);
        navigate("/campaigns"); // Navigate to campaigns page after 12 mins
        clearInterval(interval);
      }, 720000); // 12 minutes
    }
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [showNotification, campaignId, navigate]);

  useEffect(() => {
    const badge = document.querySelector(".__PrivateStripeElement");
    if (badge) badge.remove();

    return () => {
      const badge = document.querySelector(".__PrivateStripeElement");
      if (badge) badge.remove();
    };
  }, []);

  return (
    <div className="payment-form">
      <Form onFinish={handleSubmit} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter your name" }]}
        >
          <Input placeholder="Enter your name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="Enter your email" />
        </Form.Item>

        <Form.Item label="Amount">
          <Input value={`£${amount}`} disabled />
        </Form.Item>

        <Form.Item label="Card Details">
          <div className="card-wrapper">
            {!stripe || !elements ? (
              <div style={{ 
                padding: '12px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '4px',
                color: '#999',
                fontStyle: 'italic'
              }}>
                Loading Stripe...
              </div>
            ) : (
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            )}
          </div>
        </Form.Item>

        {error && <div className="payment-error">{error}</div>}

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={!stripe}
          block
        >
          Pay Now
        </Button>

        {showNotification && (
          <div className="modal" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            padding: '20px',
            border: '1px solid #ccc',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            <h3>Campaign Started</h3>
            <p>Campaign is running/sending. Wait for about 10 to 12 mins. Please wait patiently and don't close the page before it will be 'completed'.</p>
            <button
              onClick={() => setShowNotification(false)}
              style={{ marginTop: '10px', padding: '5px 10px' }}
            >
              Close
            </button>
          </div>
        )}
      </Form>
    </div>
  );
};

const PaymentsPage = () => {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(null);

  // Get campaignId from either URL parameter or query parameter
  const actualCampaignId = campaignId || searchParams.get('campaignId');
  const queryAmount = searchParams.get('amount');

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/create-payment-intent`,
          { campaignId: actualCampaignId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (res.data.success && res.data.data) {
          setClientSecret(res.data.data.clientSecret);
          setAmount(res.data.data.amount || queryAmount);
        } else {
          throw new Error(res.data.error || "Failed to create payment intent");
        }
      } catch (err) {
        console.error("❌ Failed to create payment intent", err);
      }
    };

    if (actualCampaignId) {
      fetchPaymentIntent();
    }
  }, [actualCampaignId, queryAmount]);

  if (!actualCampaignId) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <p>Error: No campaign ID provided</p>
      </div>
    );
  }

  if (!clientSecret) return (
    <div style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white'
    }}>
      <p>Loading payment details...</p>
    </div>
  );

  return (
    <div style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <div className="payment-page-container">
          <h1>Make A Payment</h1>
          <CheckoutForm clientSecret={clientSecret} campaignId={actualCampaignId} amount={amount} />
        </div>
      </Elements>
    </div>
  );
};

export default PaymentsPage;